/**
 * CLI handlers for `nexus db` commands.
 *
 * Direct database operations (not migration file management).
 */

import { spawn } from 'node:child_process'
import { isAbsolute, join } from 'node:path'
import { statSync } from 'node:fs'
import { consola } from 'consola'
import { formatBytes } from '@gzl10/nexus-sdk'
import { resolveConfig } from '@kernel/config/env.js'
import { getDatabaseType, getDatabasePath } from '@kernel/config/database.js'
import { getProjectPath } from '@kernel/config/paths.js'
import { getDb } from '@kernel/db/connection.js'
import { initCli } from './shared.js'

// ── nexus db wipe ───────────────────────────────────────────────────────────

export async function handleDbWipe(options: { yes?: boolean }): Promise<void> {
  if (process.env['NODE_ENV'] === 'production') {
    consola.warn('Cannot wipe database in production.')
    return
  }

  initCli()

  const db = getDb()
  const dbType = getDatabaseType()

  try {
    let tables: string[] = []

    if (dbType === 'sqlite') {
      const result = await db.raw("SELECT name FROM sqlite_master WHERE type='table' AND name NOT LIKE 'sqlite_%'")
      tables = result.map((r: { name: string }) => r.name)
    } else if (dbType === 'postgresql') {
      const result = await db.raw("SELECT tablename FROM pg_tables WHERE schemaname = 'public'")
      tables = result.rows.map((r: { tablename: string }) => r.tablename)
    } else if (dbType === 'mysql') {
      const result = await db.raw('SELECT TABLE_NAME FROM information_schema.tables WHERE table_schema = DATABASE()')
      tables = (result[0] || result).map((r: { TABLE_NAME: string }) => r.TABLE_NAME)
    }

    if (tables.length === 0) {
      console.log('Database is already empty')
      process.exit(0)
    }

    console.log(`\nWARNING: This will DROP ${tables.length} tables:\n`)
    for (const table of tables.slice(0, 10)) {
      console.log(`   - ${table}`)
    }
    if (tables.length > 10) {
      console.log(`   ... and ${tables.length - 10} more`)
    }
    console.log('')

    if (!options.yes) {
      const readline = await import('node:readline')
      const rl = readline.createInterface({ input: process.stdin, output: process.stdout })

      const answer = await new Promise<string>((resolve) => {
        rl.question('Type "wipe" to confirm: ', resolve)
      })
      rl.close()

      if (answer !== 'wipe') {
        console.log('Aborted')
        process.exit(0)
      }
    }

    console.log('\nDropping all tables...')

    if (dbType === 'sqlite') {
      await db.raw('PRAGMA foreign_keys = OFF')
      try {
        for (const table of tables) {
          await db.schema.dropTableIfExists(table)
        }
      } finally {
        await db.raw('PRAGMA foreign_keys = ON')
      }
    } else if (dbType === 'postgresql') {
      for (const table of tables) {
        await db.raw(`DROP TABLE IF EXISTS "${table}" CASCADE`)
      }
    } else if (dbType === 'mysql') {
      await db.raw('SET FOREIGN_KEY_CHECKS = 0')
      for (const table of tables) {
        await db.raw(`DROP TABLE IF EXISTS \`${table}\``)
      }
      await db.raw('SET FOREIGN_KEY_CHECKS = 1')
    }

    console.log(`Dropped ${tables.length} tables`)
  } catch (err) {
    console.error('Wipe failed:', err)
    process.exit(1)
  } finally {
    await db.destroy()
    process.exit(0)
  }
}

// ── nexus db shell ──────────────────────────────────────────────────────────

export function handleDbShell(): void {
  const config = resolveConfig()
  const url = config.databaseUrl
  const dbType = getDatabaseType()

  let cmd: string
  let args: string[]

  if (dbType === 'sqlite') {
    let filename = url.replace(/^(file:|sqlite:)/, '')
    if (!isAbsolute(filename)) {
      filename = join(getProjectPath(), 'data', filename)
    }
    cmd = 'sqlite3'
    args = [filename]
  } else if (dbType === 'postgresql') {
    cmd = 'psql'
    args = [url]
  } else {
    // MySQL
    const parsed = new URL(url)
    cmd = 'mysql'
    args = [
      '-h', parsed.hostname,
      '-P', parsed.port || '3306',
      '-u', parsed.username,
      ...(parsed.password ? [`-p${parsed.password}`] : []),
      parsed.pathname.slice(1),
    ]
  }

  console.log(`Opening ${dbType} shell...`)

  const child = spawn(cmd, args, { stdio: 'inherit' })
  child.on('exit', (code) => process.exit(code ?? 0))
}

// ── nexus db info ───────────────────────────────────────────────────────────

export interface DbInfo {
  type: string
  connection: string
  tables: number
  size: string | null
}

export async function collectDbInfo(): Promise<DbInfo> {
  initCli()

  const dbType = getDatabaseType()
  const dbPath = getDatabasePath()
  const db = getDb()

  let tables = 0
  let size: string | null = null

  try {
    if (dbType === 'sqlite') {
      const result = await db.raw("SELECT count(*) as cnt FROM sqlite_master WHERE type='table' AND name NOT LIKE 'sqlite_%'")
      tables = result[0]?.cnt ?? 0

      const url = resolveConfig().databaseUrl
      if (url !== ':memory:' && !url.includes(':memory:')) {
        let filename = url.replace(/^(file:|sqlite:)/, '')
        if (!isAbsolute(filename)) {
          filename = join(getProjectPath(), 'data', filename)
        }
        try {
          const stat = statSync(filename)
          size = formatBytes(stat.size)
        } catch { /* file may not exist yet */ }
      }
    } else if (dbType === 'postgresql') {
      const tablesResult = await db.raw("SELECT count(*) as cnt FROM pg_tables WHERE schemaname = 'public'")
      tables = parseInt(tablesResult.rows[0]?.cnt ?? '0')
      const sizeResult = await db.raw("SELECT pg_size_pretty(pg_database_size(current_database())) as size")
      size = sizeResult.rows[0]?.size ?? null
    } else if (dbType === 'mysql') {
      const tablesResult = await db.raw('SELECT count(*) as cnt FROM information_schema.tables WHERE table_schema = DATABASE()')
      tables = (tablesResult[0] || tablesResult)[0]?.cnt ?? 0
      const sizeResult = await db.raw("SELECT ROUND(SUM(data_length + index_length) / 1024 / 1024, 2) as size_mb FROM information_schema.tables WHERE table_schema = DATABASE()")
      const sizeMb = (sizeResult[0] || sizeResult)[0]?.size_mb
      size = sizeMb ? `${sizeMb} MB` : null
    }
  } finally {
    await db.destroy()
  }

  return { type: dbType, connection: dbPath, tables, size }
}

export async function handleDbInfo(): Promise<void> {
  const info = await collectDbInfo()

  console.log('')
  console.log(`  Database: ${info.type}`)
  console.log(`  Connection: ${info.connection}`)
  console.log(`  Tables: ${info.tables}`)
  if (info.size) {
    console.log(`  Size: ${info.size}`)
  }
  console.log('')
}
